import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaBarcode, FaFolderOpen, FaLock, FaPrint } from "react-icons/fa";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { converterDataHoraBr } from "_utils/Data";
import { ILoteEstampagem, StatusLote } from "../../interfaces/ILoteEstampagem";
import { Container, Header, TextoHeader } from "./style";

const iconStyle: React.CSSProperties = { width: 14, height: 14, display: "block" };
const iconBtn: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer", padding: "2px 4px", lineHeight: 1,
};

const statusLabel: Record<StatusLote, string> = {
  ABERTO:     "Aberto",
  FECHADO:    "Fechado",
  ENVIADO:    "Enviado",
  ENTREGUE:   "Entregue",
  FINALIZADO: "Finalizado",
};

const ALL_STATUSES: StatusLote[] = ["ABERTO", "FECHADO", "ENVIADO", "ENTREGUE", "FINALIZADO"];

const statusColor: Record<StatusLote, string> = {
  ABERTO:     "#198754",
  FECHADO:    "#6c757d",
  ENVIADO:    "#0d6efd",
  ENTREGUE:   "#0dcaf0",
  FINALIZADO: "#212529",
};

interface IListarProps {
  lotes: ILoteEstampagem[];
  loading: boolean;
  loadingFinalizarId: number | null;
  loadingGuiaId: number | null;
  loadingAvancarId: number | null;
  loadingArId: number | null;
  loadingRastreioId: number | null;
  selecionados: number[];
  onToggleSelecionado: (id: number) => void;
  onToggleTodos: () => void;
  onFinalizar: (id: number) => void;
  onImprimirGuia: (id: number) => void;
  onAvancarStatus: (id: number) => void;
  onVincularAr: (id: number) => void;
  onAbrirRastreio: (id: number) => void;
}

export function Listar({ lotes, loading, loadingFinalizarId, loadingGuiaId, loadingAvancarId, loadingArId, loadingRastreioId, selecionados, onToggleSelecionado, onToggleTodos, onFinalizar, onImprimirGuia, onAvancarStatus, onVincularAr, onAbrirRastreio }: IListarProps) {
  const navigate = useNavigate();
  const todosIds = lotes.map((l) => l.id);
  const todosSelecionados = todosIds.length > 0 && todosIds.every((id) => selecionados.includes(id));

  const columns: IColum<ILoteEstampagem>[] = [
    {
      name: (
        <input
          type="checkbox"
          checked={todosSelecionados}
          onChange={onToggleTodos}
          title="Selecionar todos"
          style={{ cursor: "pointer", width: 16, height: 16 }}
        />
      ),
      selector: (lote: ILoteEstampagem) => (
        <input
          type="checkbox"
          checked={selecionados.includes(lote.id)}
          onChange={() => onToggleSelecionado(lote.id)}
          style={{ cursor: "pointer", width: 16, height: 16 }}
        />
      ),
      width: "50px",
    },
    {
      name: <span>ID</span>,
      selector: ({ id }: ILoteEstampagem) => id,
      width: "60px",
    },
    {
      name: <span>Empresa</span>,
      selector: ({ nomeEmpresa }: ILoteEstampagem) => nomeEmpresa,
    },
    {
      name: <span>Local de Entrega</span>,
      selector: ({ localEntrega }: ILoteEstampagem) => localEntrega ?? <span style={{ color: "#adb5bd", fontSize: "0.8rem" }}>—</span>,
    },
    {
      name: <span>AR</span>,
      selector: (lote: ILoteEstampagem) => lote.ar ? (
        <button
          title="Ver rastreio nos Correios"
          disabled={loadingRastreioId === lote.id}
          style={{ background: "none", border: "none", padding: 0, color: "#0d6efd", cursor: "pointer", textDecoration: "underline", fontSize: "0.82rem", fontFamily: "monospace" }}
          onClick={() => onAbrirRastreio(lote.id)}
        >
          {loadingRastreioId === lote.id ? <Spinner animation="border" size="sm" /> : lote.ar}
        </button>
      ) : (
        <span style={{ color: "#adb5bd", fontSize: "0.8rem" }}>—</span>
      ),
      width: "140px",
    },
    {
      name: <span>Status</span>,
      selector: (lote: ILoteEstampagem) => {
        const isLoading = loadingAvancarId === lote.id || loadingFinalizarId === lote.id;
        return (
          <select
            value={lote.status}
            disabled={isLoading}
            style={{
              border: "1px solid " + statusColor[lote.status],
              color: statusColor[lote.status],
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: "0.78rem",
              fontWeight: 600,
              background: "white",
              cursor: "pointer",
            }}
            onChange={(e) => {
              const novo = e.target.value as StatusLote;
              if (novo === "FECHADO") { onFinalizar(lote.id); }
              else { onAvancarStatus(lote.id); }
            }}
          >
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel[s]}
              </option>
            ))}
          </select>
        );
      },
      width: "140px",
    },
    {
      name: <span>Criado em</span>,
      selector: ({ criadoEm }: ILoteEstampagem) => criadoEm ? converterDataHoraBr(criadoEm) : "-",
      width: "150px",
    },
    {
      name: <span>Ações</span>,
      selector: (lote: ILoteEstampagem) => (
        <div className="d-flex gap-1 align-items-center">
          <button
            title="Abrir lote"
            style={{ ...iconBtn, color: "#0d6efd" }}
            onClick={() => navigate(`/backoffice/estampagem-placa/${lote.id}`)}
          >
            <FaFolderOpen style={iconStyle} />
          </button>
          {lote.status === "ABERTO" && (
            <button
              title="Fechar lote"
              style={{ ...iconBtn, color: "#6c757d" }}
              disabled={loadingFinalizarId === lote.id}
              onClick={() => onFinalizar(lote.id)}
            >
              {loadingFinalizarId === lote.id ? <Spinner animation="border" size="sm" /> : <FaLock style={iconStyle} />}
            </button>
          )}
          <button
            title={lote.ar ? "Atualizar AR" : "Vincular AR"}
            style={{ ...iconBtn, color: lote.ar ? "#fd7e14" : "#6f42c1" }}
            disabled={loadingArId === lote.id}
            onClick={() => onVincularAr(lote.id)}
          >
            {loadingArId === lote.id ? <Spinner animation="border" size="sm" /> : <FaBarcode style={iconStyle} />}
          </button>
          <button
            title="Imprimir guia"
            style={{ ...iconBtn, color: "#6f42c1" }}
            disabled={loadingGuiaId === lote.id}
            onClick={() => onImprimirGuia(lote.id)}
          >
            {loadingGuiaId === lote.id ? <Spinner animation="border" size="sm" /> : <FaPrint style={iconStyle} />}
          </button>
        </div>
      ),
      width: "130px",
    },
  ];

  return (
    <Container>
      <Header>
        <TextoHeader>Estampagens de Placa</TextoHeader>
      </Header>
      <hr />
      <Table
        keyField="id"
        columns={columns}
        data={lotes}
        loading={loading}
        pagination={false}
        noSubHeader
      />
    </Container>
  );
}
